import { useEffect, useRef, useState } from "react";
import { Eye, Hand, Play, Pause } from "lucide-react";

// Visual demonstration of the two bilateral methods. Rendered inline on
// task 1 of every practice.
//   • Eyes tab → real video of a left↔right eye sweep (eye-sweep.mp4).
//   • Hands tab → real video of alternating finger taps (hand-tap.mp4).
// Both videos auto-pause when the user switches tabs, and the single
// Play / Pause button drives whichever is currently visible.

export const BilateralDemo = () => {
  const [playing, setPlaying] = useState(false);
  const [mode, setMode] = useState("eyes"); // "eyes" | "hands"
  const eyeVideoRef = useRef(null);
  const handVideoRef = useRef(null);

  // On mount, seek the eye video to ~15% of its duration so the still
  // frame the user first sees has the eyes glancing off to the side
  // rather than staring straight at them. Only runs once (when paused
  // and at t=0); after the user has played the video we don't re-seek.
  const handleEyeMetadata = () => {
    const eye = eyeVideoRef.current;
    if (!eye) return;
    if (!playing && eye.currentTime === 0 && eye.duration > 0) {
      eye.currentTime = Math.min(eye.duration * 0.15, eye.duration - 0.05);
    }
  };
  // Drive the currently-visible video from the shared play/pause state.
  // The off-screen video is always paused so we don't waste decoder
  // cycles or play unheard audio (both are muted anyway).
  useEffect(() => {
    const eye = eyeVideoRef.current;
    const hand = handVideoRef.current;
    const active = mode === "eyes" ? eye : hand;
    const inactive = mode === "eyes" ? hand : eye;
    if (inactive) inactive.pause();
    if (!active) return;
    if (playing) {
      active.play().catch(() => {
        /* autoplay blocked is fine — user can hit play again */
      });
    } else {
      active.pause();
    }
  }, [playing, mode]);

  return (
    <div
      className="mb-8 rounded-2xl border border-slate-100 bg-[#FAFBFC] overflow-hidden"
      data-testid="bilateral-demo-card"
    >
      {/* Header / toggle row */}
      <div className="flex items-center justify-between gap-3 px-5 pt-5">
        <div>
          <p className="text-xs font-medium text-[#3090A8] uppercase tracking-[0.14em] mb-1">
            Quick demo
          </p>
          <h4 className="font-outfit font-medium text-[#1B2349]">
            See what it looks like
          </h4>
        </div>
        <button
          onClick={() => setPlaying((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1B2349] text-white text-sm font-medium hover:bg-[#0F1838] transition-colors"
          data-testid="bilateral-demo-play"
          aria-label={playing ? "Pause demo" : "Play demo"}
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          {playing ? "Pause" : "Play"}
        </button>
      </div>

      {/* Tab toggles — Eyes vs Hands */}
      <div className="flex gap-2 px-5 mt-4">
        <button
          onClick={() => setMode("eyes")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            mode === "eyes"
              ? "bg-[#EAF0F9] text-[#1B2349]"
              : "text-[#718096] hover:text-[#1B2349]"
          }`}
          data-testid="bilateral-demo-tab-eyes"
        >
          <Eye className="w-3.5 h-3.5" />
          Eye movement
        </button>
        <button
          onClick={() => setMode("hands")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            mode === "hands"
              ? "bg-[#FEF3E7] text-[#1B2349]"
              : "text-[#718096] hover:text-[#1B2349]"
          }`}
          data-testid="bilateral-demo-tab-hands"
        >
          <Hand className="w-3.5 h-3.5" />
          Hand tapping
        </button>
      </div>

      {/* Stage */}
      <div className="px-5 pb-6 pt-4">
        {mode === "eyes" ? (
          <div
            className="relative h-32 sm:h-48 rounded-xl bg-black overflow-hidden mx-auto max-w-md"
            data-testid="bilateral-demo-eye-stage"
          >
            {/* Real video of an eye sweep — re-encoded for the web with
                light denoise + brightness lift. Loops continuously while
                playing; muted + playsInline so it works on iOS Safari. */}
            <video
              ref={eyeVideoRef}
              src="/videos/demo/eye-sweep.mp4"
              poster="/videos/demo/eye-sweep-poster.jpg"
              className="absolute inset-0 w-full h-full object-cover"
              loop
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={handleEyeMetadata}
              data-testid="bilateral-demo-eye-video"
            >
              <source src="/videos/demo/eye-sweep.webm" type="video/webm" />
              <source src="/videos/demo/eye-sweep.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/65 to-transparent pointer-events-none" />
            <div className="absolute bottom-2 left-0 right-0 text-center text-[11px] text-white/95">
              Head still · eyes sweep all the way left ↔ right
            </div>
          </div>
        ) : (
          <div
            className="relative h-32 sm:h-48 rounded-xl bg-black overflow-hidden mx-auto max-w-md"
            data-testid="bilateral-demo-hand-stage"
          >
            {/* Real video of alternating finger taps — re-encoded for
                the web with heavy denoise (softens skin) + vignette
                (blurs / darkens the periphery). object-contain so the
                portrait framing isn't hard-cropped at the top/bottom. */}
            <video
              ref={handVideoRef}
              src="/videos/demo/hand-tap.mp4"
              poster="/videos/demo/hand-tap-poster.jpg"
              className="absolute inset-0 w-full h-full object-contain"
              loop
              muted
              playsInline
              preload="metadata"
              data-testid="bilateral-demo-hand-video"
            >
              <source src="/videos/demo/hand-tap.webm" type="video/webm" />
              <source src="/videos/demo/hand-tap.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/65 to-transparent pointer-events-none" />
            <div className="absolute bottom-2 left-0 right-0 text-center text-[11px] text-white/95">
              Palms rest on top of thighs · fingers tap left ↔ right
            </div>
          </div>
        )}

        {/* Plain-language description below the animation */}
        <p className="text-[13px] text-[#4A5568] leading-relaxed mt-4">
          {mode === "eyes" ? (
            <>
              Keep your head perfectly still and let your eyes sweep smoothly all
              the way from left to right and back. Imagine you're watching a
              tennis ball bounce across the court.
            </>
          ) : (
            <>
              Rest your palms gently on the tops of your thighs and keep them
              there. Tap with the fingers of your left hand, then the fingers
              of your right hand, in steady alternation with the tone. You can
              close your eyes while you tap.
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default BilateralDemo;
